import React, { use, useEffect, useState } from "react";

// custom components
import JobList from "../job-components/job-list";
import EmployeeInformation from "../employee-components/employee-information";
import TimelineColumnContainer from "./timeline-column-container";

// styling
import { Colors } from "_styles";

// time formatting
const timeFormatter = require("_utils/date-time-functions");

// timeline constants
import { EmployeeInformationColumnWidth } from "../../../../utils/timeline-constants";

// redux
import { useSelector, useDispatch } from "react-redux";
import { setShowConfirmationModal, selectSelectedTasksList, setShowBreakTaskModal, setBreakTaskEmployee } from "_redux/slices/timeline/selected-task";
import { selectThirtyMinLayout, selectTimelineDate } from "_redux/slices/timeline/timeline-control";
import { selectTimelineData } from "_redux/slices/timeline/timeline-data";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// functions 
import { convertShiftTimeStamp, convertTimeStampToDate, getDate } from "_utils/functions";
import { setClickedEmployeeId } from "_redux/slices/timeline/employees";

const TimelineRow = (props) => {

  //#region redux

  const dispatch = useDispatch();
  const timelineData = useSelector(selectTimelineData);                                 // timeline data object
  const selectedTasksList = useSelector(selectSelectedTasksList);
  const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
  const usedTheme = useSelector(selectUsedTheme);
  // const showBreakTaskModal = useSelector(selectShowBreakTaskModal);

  //#endregion

  //#region local states
  const [timelineHour, setTimelineHour] = useState(0);                            // holds current hour
  const [timelinePercentage, setTimelinePercentage] = useState(0);                // holds position of line relative to minute in hour
  const [shiftStart, setShiftStart] = useState("")                                // holds value of employee shift start time
  const [shiftEnd, setShiftEnd] = useState("")                                    // holds value of employee shift end time
  const [shiftHours, setShiftHours] = useState([])                                    // holds value of employee shift end time


  //#endregion

  //#region useEffects

  // draw time line //
  useEffect(() => {
    const updateTimeline = () => {
      const currentDate = new Date();
      const currentHour = currentDate.getHours();
      const currentMinute = currentDate.getMinutes();
      setTimelinePercentage((currentMinute * 100) / 60);
      setTimelineHour(timeFormatter.formatHour(currentHour));
    };

    // Initial update
    updateTimeline();

    // Schedule updates every 60 seconds
    const intervalId = setInterval(updateTimeline, 60000);

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (dbUser.details.authority === "JER") {
      if (props?.employee?.shift) {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }
        let tempStartsShiftDate = convertTimeStampToDate(props?.employee?.shift?.start, options)
        let startShiftYear = tempStartsShiftDate?.getFullYear();
        let startShiftDay = tempStartsShiftDate?.getDay();
        let currentYear = new Date(timelineDate).getFullYear();
        let currentDay = new Date(timelineDate).getDay();
        // check if employee shift is in current day 
        if (startShiftYear == currentYear && startShiftDay == currentDay) {
          setShiftStart(convertShiftTimeStamp(props?.employee?.shift?.start))
          setShiftEnd(convertShiftTimeStamp(props?.employee?.shift?.end))
        }
      }
    } else {
      setShiftStart(props?.employee?.shift ? new Date(props?.employee?.shift?.start?.seconds * 1000) : null);
      setShiftEnd(props?.employee?.shift ? new Date(props?.employee?.shift?.end?.seconds * 1000) : null);
      if (props?.employee?.shift) {
        let tempShiftHours = generateShiftHoursArray(props?.employee?.shift?.start, props?.employee?.shift?.end)
        setShiftHours(tempShiftHours)
      }

    }


  }, [props?.employee?.shift, timelineDate]);


  //#endregion

  //#region functions

  // get the time line row clicked column
  const handleColumnClick = (column) => {
    // check if there is a selcted task list to assign
    if (Array.isFullArray(selectedTasksList)) {
      /* create id to save which column was clicked */
      const columnId = column + "-" + props?.employee?.employeeId;
      props?.setClickedColumn(columnId);
      // show confirmation modal
      dispatch(setShowConfirmationModal(true));
    }
    if (props.employee.userId) {
      dispatch(setClickedEmployeeId(props.employee.userId))
    }
  };

  const openBreakTaskModal = () => {
    dispatch(setShowBreakTaskModal(true))
    dispatch(setBreakTaskEmployee(props?.employee))
  };

  function generateShiftHoursArray(startTimestamp, endTimestamp) {
    const hoursArray = [];

    // Convert Firestore timestamps to Date objects
    const startDate = new Date(startTimestamp?.seconds * 1000);
    const endDate = new Date(endTimestamp?.seconds * 1000);

    // Handle case where end time is next day (overnight shift)
    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    // Current hour we're processing
    let currentHour = new Date(startDate);

    // Format helper function
    const formatHour = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      // return `${year}-${month}-${day}_${hours}:${minutes}`;
      return `${year}-${month}-${day}_${hours}`;

    };

    // Add each hour until we reach end time
    while (currentHour <= endDate) {
      hoursArray.push(formatHour(currentHour));
      currentHour = new Date(currentHour.getTime() + 60 * 60 * 1000); // Add 1 hour
    }

    return hoursArray;
  }

  function isSameDay(d1, d2) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }
  //#endregion


  return (
    <>
      <div style={{ display: "flex" }}>

        {/* employee profile pic, name etc. */}
        <div style={{ position: "sticky", left: 0, zIndex: 200 }}>
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", height: "100%", width: EmployeeInformationColumnWidth, borderRight: "1px solid ${usedTheme.BORDER_COLOR}", backgroundColor: usedTheme.BACKGROUND_COLOR }}>
            <EmployeeInformation id={props?.employee?.employeeId} employeeShift={props?.employee?.shift} openBreakTaskModal={openBreakTaskModal} employee={props?.employee} index={props?.index} employeeArrayLength={props?.arrayLength} />
          </div>
        </div>

        {/* timeline columns */}
        {timelineData && timelineData?.columnIds && timelineData?.columnIds.map && timelineData?.columnIds?.map((columnId, index) => {
          return (
            <div key={index} >
              <TimelineColumnContainer
                // backgroundColor={columnId >= shiftStart && columnId <= shiftEnd ? "#F5F5F5" : "white"}
                backgroundColor={
                  dbUser.details.authority === "JER"
                    ? columnId >= shiftStart && columnId <= shiftEnd ? usedTheme.TIMELINE_COLOR : usedTheme.BACKGROUND_COLOR
                    : shiftHours?.includes(`${getDate(timelineDate)}_${columnId?.split(":")?.[0]}`) ? usedTheme.TIMELINE_COLOR : usedTheme.BACKGROUND_COLOR

                }
                // backgroundColor={columnId >= shiftStart && columnId <= shiftEnd ? usedTheme.TIMELINE_COLOR : usedTheme.BACKGROUND_COLOR}
                borderColor={props?.clickedColumn == columnId + "-" + props?.employee?.employeeId ? Colors.PRM_LIGHT_BLUE : usedTheme.BORDER_COLOR}
                onClick={() => { handleColumnClick(columnId) }}
                is30MinLayout={thirtyMinLayout}
              >

                {/* Red line div */}
                {columnId == timelineHour && isSameDay(new Date(), timelineDate) && (
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: `${timelinePercentage}%`, width: "2px", backgroundColor: "red" }} />
                )}

                {/* list of employee jobs within column */}
                <JobList employeeId={props?.employee?.employeeId} hour={columnId} id={columnId + "-" + props?.employee?.employeeId} disabled={true} />

              </TimelineColumnContainer>
            </div>


          );
        })}

      </div>
    </>
  );
};

export default TimelineRow;
